import Box from "@mui/material/Box";
import { colors } from "@/theme/colors";
import { fonts } from "@/theme/tokens";

/**
 * A realistic-looking iOS app screen inside an iPhone frame — ink bezel with a
 * dynamic island, a status bar, a violet balance card, list rows, and a tab
 * bar. Pure DOM/CSS on the site palette; decorative only. Sized by its parent.
 */
export function PhoneMock() {
  return (
    <Box
      sx={{
        width: "100%",
        aspectRatio: "1 / 2.04",
        borderRadius: "26px",
        backgroundColor: colors.textPrimary,
        p: "6px",
        position: "relative",
        boxShadow:
          "0 26px 48px -16px rgba(17,24,39,0.42), 0 10px 18px -10px rgba(17,24,39,0.30)",
      }}
    >
      {/* Dynamic island. */}
      <Box
        sx={{
          position: "absolute",
          top: 12,
          left: "50%",
          transform: "translateX(-50%)",
          width: "34%",
          height: 12,
          borderRadius: "999px",
          backgroundColor: "#000",
          zIndex: 2,
        }}
      />
      {/* Screen. */}
      <Box
        sx={{
          height: "100%",
          borderRadius: "21px",
          overflow: "hidden",
          backgroundColor: colors.paper,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Status bar. */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 1.5,
            pt: 1,
            pb: 0.5,
          }}
        >
          <Box
            component="span"
            sx={{
              fontFamily: fonts.mono,
              fontSize: "0.5rem",
              fontWeight: 600,
              color: colors.textPrimary,
            }}
          >
            9:41
          </Box>
          <Box sx={{ display: "flex", gap: 0.4, alignItems: "flex-end" }}>
            {[3, 5, 7].map((h) => (
              <Box
                key={h}
                sx={{
                  width: 2.5,
                  height: h,
                  borderRadius: 0.5,
                  backgroundColor: colors.textPrimary,
                }}
              />
            ))}
            <Box
              sx={{
                width: 12,
                height: 6,
                borderRadius: 1,
                border: `1px solid ${colors.textPrimary}`,
                ml: 0.25,
              }}
            />
          </Box>
        </Box>

        {/* App header. */}
        <Box sx={{ px: 1.5, pt: 0.5 }}>
          <Box
            component="span"
            sx={{
              fontFamily: fonts.mono,
              fontSize: "0.45rem",
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              color: colors.primary,
            }}
          >
            Today
          </Box>
          <Box
            sx={{
              fontFamily: fonts.heading,
              fontSize: "0.85rem",
              fontWeight: 500,
              color: colors.textPrimary,
              lineHeight: 1.1,
              mt: 0.25,
            }}
          >
            Dashboard
          </Box>
        </Box>

        {/* Balance card. */}
        <Box
          sx={{
            mx: 1.5,
            mt: 1,
            p: 1.25,
            borderRadius: "12px",
            background: `linear-gradient(140deg, ${colors.primary}, ${colors.boost})`,
            color: "#fff",
          }}
        >
          <Box
            component="span"
            sx={{
              fontFamily: fonts.mono,
              fontSize: "0.4rem",
              letterSpacing: "0.1em",
              opacity: 0.85,
            }}
          >
            BALANCE
          </Box>
          <Box
            sx={{
              fontFamily: fonts.heading,
              fontSize: "1.05rem",
              fontWeight: 500,
              mt: 0.25,
            }}
          >
            $12,480
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, mt: 0.75 }}>
            <Box
              sx={{
                flex: 2,
                height: 4,
                borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.85)",
              }}
            />
            <Box
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.35)",
              }}
            />
          </Box>
        </Box>

        {/* List rows. */}
        <Box
          sx={{
            px: 1.5,
            mt: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.9,
            flex: 1,
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "6px",
                  backgroundColor: colors.tintDeep,
                  border: `1px solid ${colors.divider}`,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    height: 5,
                    width: "70%",
                    borderRadius: 1,
                    backgroundColor: "#e5e7eb",
                  }}
                />
                <Box
                  sx={{
                    height: 4,
                    width: "45%",
                    borderRadius: 1,
                    backgroundColor: "#eef0f2",
                    mt: 0.5,
                  }}
                />
              </Box>
              <Box
                sx={{
                  height: 5,
                  width: 18,
                  borderRadius: 1,
                  backgroundColor: "#e5e7eb",
                }}
              />
            </Box>
          ))}
        </Box>

        {/* Tab bar. */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            height: 34,
            borderTop: `1px solid ${colors.divider}`,
          }}
        >
          {[0, 1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                width: 14,
                height: 14,
                borderRadius: "4px",
                backgroundColor: i === 0 ? colors.primary : "#d8dade",
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
